import axios from "axios";
import { toast } from "react-toastify";
import { Row, Col, Form, Button } from "react-bootstrap";
import FloatingLabel from "react-bootstrap-floating-label";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import citys from "../../constans/ciudades.json";
import { URL_CREDIT_NOTE_REQUEST, URL_INVOICE_QR_READ } from "../../constans/servidor";
import UploadElectronicInvoiceFile from "../../utils/UploadElectronicInvoiceFile";
import { parsePrefixNumberFromElectronicInvoice } from "../../utils/parseElectronicInvoiceFromQr";
import { isInvoiceImageFile } from "../../utils/validateInvoicePdf";
import ModalCompanyPolicies from "../../components/modal/modalPolicies";

const MSG_NUMERO_FACTURA_NO_COINCIDE =
  "El número de factura no coincide con la factura cargada";
const MSG_QR_NO_COINCIDE =
  "El prefijo o número no coinciden con la factura leída del código QR";
const MSG_QR_SIN_DATOS =
  "No pudimos leer el prefijo y número de factura desde el QR de la imagen.";
const TOAST_ID_NUMERO_FACTURA = "numero-factura-pdf-mismatch";
const TOAST_ID_QR_MISMATCH = "invoice-qr-mismatch";
const TOAST_ID_QR_PARSE = "invoice-qr-parse";
const TOAST_ID_INVOICE_CHANGE_ERROR = "invoice-change-error";
const MSG_SOLICITUD_FACTURA_EXITO =
  "La nueva factura será enviada al correo registrado";
const TOAST_ID_SOLICITUD_FACTURA_EXITO = "invoice-request-success";

function showInvoiceChangeError(message, toastId) {
  toast.dismiss(toastId);
  toast(
    <div className="invoice-toast-error-inner">
      <span className="invoice-toast-error-icon" aria-hidden>
        !
      </span>
      <span className="invoice-toast-error-text">{message}</span>
    </div>,
    {
      className: "invoice-toast-error",
      bodyClassName: "invoice-toast-error-body",
      toastId,
      containerId: "invoice-change-toast",
      type: "default",
    }
  );
}

const useInvoiceChangeForm = () => {
  const form = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      tipo: { value: "1", label: "CC" },
      ciudad: null,
      prefijo: "",
      numeroFactura: "",
      numeroDocumento: "",
      nombre: "",
      correo: "",
      celular: "",
      acepta: false,
      archivo: null,
      pdfPrefijo: "",
      pdfNumeroFactura: ""
    },
  });

  return form;
};

const ChangeInvoiceComponent = () => {
  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    register,
    reset,
    watch,
    formState: { errors, isDirty }
  } = useInvoiceChangeForm();

  const [optionsIdentificacion, setOptionsIdentificacion] = useState([]);
  const [optionsCiudad, setOptionsCiudad] = useState([]);
  const [name, setName] = useState(null);
  const [resetUpload, setResetUpload] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showPoliciesModal, setShowPoliciesModal] = useState(false);
  const [policiesActiveTab, setPoliciesActiveTab] = useState("privacidad");
  const filled = watch();
  const pdfPrefijo = watch("pdfPrefijo");
  const pdfNumeroFactura = watch("pdfNumeroFactura");
  const selectStyles = {
    container: (provided) => ({ ...provided, width: "100%" }),
    control: (provided, state) => ({
      ...provided,
      minHeight: "46px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "rgba(144, 212, 0, 0.55)" : "#d9d9d9",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(144, 212, 0, 0.12)" : "none",
      backgroundColor: "#fff",
    }),
    placeholder: (provided) => ({ ...provided, color: "rgba(0,0,0,0.35)" }),
    singleValue: (provided) => ({ ...provided, color: "rgba(0,0,0,0.55)" }),
    valueContainer: (provided) => ({ ...provided, padding: "0 12px" }),
    indicatorSeparator: (provided) => ({ ...provided, display: "none" }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  };

  useEffect(() => {
    setOptionsIdentificacion([
      { value: "1", label: "CC" },
      { value: "2", label: "CE" },
      { value: "3", label: "NIT" }
    ]);

    setOptionsCiudad(citys.map(item => ({ value: item.id, label: item.ciudad })));

    setValue("tipo", { value: "1", label: "CC" });
  }, [setValue]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    const prefix = String(data.prefijo ?? "").trim().toUpperCase();
    const invoiceNumber = String(data.numeroFactura ?? "").trim();
    const files = data.archivo;
    const file = Array.isArray(files) ? files[0] : files;

    if (!file) {
      showInvoiceChangeError("Adjunta la factura (PDF o imagen).", TOAST_ID_INVOICE_CHANGE_ERROR);
      setIsLoading(false);
      return;
    }

    const payload = {
      prefix,
      invoiceNumber,
      documentType: data.tipo?.label ?? "",
      documentNumber: String(data.numeroDocumento ?? "").trim(),
      customerName: String(data.nombre ?? "").trim(),
      email: String(data.correo ?? "").trim(),
      phone: String(data.celular ?? "").trim(),
      city: data.ciudad?.label ?? "",
      acceptedTerms: Boolean(data.acepta),
    };

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
      } else {
        formData.append(key, value);
      }
    });
    const safeName = `${prefix}-${invoiceNumber}`.replace(/[^\w.-]+/g, "_");
    const nameLower = String(file.name || "").toLowerCase();
    const extFromName = /\.(pdf|jpe?g|png|webp|gif)$/i.test(file.name || "")
      ? nameLower.slice(nameLower.lastIndexOf("."))
      : file.type === "application/pdf"
        ? ".pdf"
        : file.type === "image/jpeg"
          ? ".jpg"
          : file.type === "image/png"
            ? ".png"
            : file.type === "image/webp"
              ? ".webp"
              : file.type === "image/gif"
                ? ".gif"
                : ".bin";
    const isImage = isInvoiceImageFile(file);
    let qrCufe = "";

    try {
      if (isImage) {
        const qrForm = new FormData();
        qrForm.append("image", file, `${safeName}${extFromName}`);
        const qrRes = await axios.post(URL_INVOICE_QR_READ, qrForm);
        const body = qrRes.data ?? {};
        qrCufe = body.cufe != null ? String(body.cufe) : "";
        const fromQr = parsePrefixNumberFromElectronicInvoice(body.electronicInvoice);
        if (!fromQr) {
          showInvoiceChangeError(MSG_QR_SIN_DATOS, TOAST_ID_QR_PARSE);
          return;
        }
        const qrPrefix = String(fromQr.prefix ?? "").trim().toUpperCase();
        const qrDigits = String(fromQr.number ?? "").replace(/[^\d]/g, "");
        const formDigits = String(data.numeroFactura ?? "").replace(/[^\d]/g, "");
        if (qrPrefix !== prefix || qrDigits !== formDigits) {
          showInvoiceChangeError(MSG_QR_NO_COINCIDE, TOAST_ID_QR_MISMATCH);
          return;
        }
      }

      formData.append(isImage ? "image" : "pdf", file, `${safeName}${extFromName}`);
      const result = await axios.post(URL_CREDIT_NOTE_REQUEST, formData);
      console.log("result:", result);
      if (result.status === 201) {
        toast.dismiss(TOAST_ID_SOLICITUD_FACTURA_EXITO);
        toast(
          <div className="invoice-toast-success-inner">
            <span className="invoice-toast-success-icon" aria-hidden>
              ✓
            </span>
            <span className="invoice-toast-success-text">
              {MSG_SOLICITUD_FACTURA_EXITO}
            </span>
          </div>,
          {
            className: "invoice-toast-success",
            bodyClassName: "invoice-toast-success-body",
            toastId: TOAST_ID_SOLICITUD_FACTURA_EXITO,
            containerId: "invoice-change-toast",
            type: "default",
          }
        );
        setName(null);
        setResetUpload((prev) => prev + 1);
        reset();
        setIsLoading(false);
      } else {
        showInvoiceChangeError(
          result.data?.message || "No se pudo registrar la solicitud.",
          TOAST_ID_INVOICE_CHANGE_ERROR
        );
        setIsLoading(false);
      }
    } catch (error) {
      console.log("Error al enviar el formulario:", error);
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Error al enviar el formulario";
      showInvoiceChangeError(msg, TOAST_ID_INVOICE_CHANGE_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  const onInvalid = (formErrors) => {
    console.log("Errores formulario:", formErrors);
    if (formErrors.numeroFactura?.message === MSG_NUMERO_FACTURA_NO_COINCIDE) {
      toast.dismiss(TOAST_ID_NUMERO_FACTURA);
      toast(
        <div className="invoice-toast-warning-inner">
          <span className="invoice-toast-warning-icon" aria-hidden>
            !
          </span>
          <span className="invoice-toast-warning-text">
            {MSG_NUMERO_FACTURA_NO_COINCIDE}
          </span>
        </div>,
        {
          className: "invoice-toast-warning",
          bodyClassName: "invoice-toast-warning-body",
          toastId: TOAST_ID_NUMERO_FACTURA,
          containerId: "invoice-change-toast",
          type: "default",
        }
      );
    }
  };

  const openPoliciesModal = (tab, e) => {
    e.preventDefault();
    e.stopPropagation();
    setPoliciesActiveTab(tab);
    setShowPoliciesModal(true);
  };

  return (
    <>
      <Form className="p-3 change-invoice-form" onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <Row className="gx-2">
          <Col xs={12} sm={4}>
            <div className={`change-invoice-field ${filled?.prefijo ? "is-filled" : ""}`}>
              <Controller
                name="prefijo"
                control={control}
                rules={{
                  required: "Campo requerido",
                  validate: (value) => {
                    if (!pdfPrefijo) return true;
                    return String(value || "").toUpperCase() === String(pdfPrefijo || "").toUpperCase()
                      || "El prefijo no coincide con la factura cargada";
                  }
                }}
                render={({ field }) => (
                  <FloatingLabel
                    label="Prefijo*"
                    type="text"
                    style={{ width: "100%" }}
                    initialValue={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.prefijo && <small className="text-danger form-error">{errors.prefijo.message}</small>}
            </div>
          </Col>

          <Col xs={12} sm={4}>
            <div className={`change-invoice-field ${filled?.numeroFactura ? "is-filled" : ""}`}>
              <Controller
                name="numeroFactura"
                control={control}
                rules={{
                  required: "Campo requerido",
                  validate: (value) => {
                    if (!pdfNumeroFactura) return true;
                    const v = String(value ?? "").replace(/[^\d]/g, "");
                    const p = String(pdfNumeroFactura ?? "").replace(/[^\d]/g, "");
                    if (v === p) {
                      toast.dismiss(TOAST_ID_NUMERO_FACTURA);
                      return true;
                    }
                    return MSG_NUMERO_FACTURA_NO_COINCIDE;
                  }
                }}
                render={({ field }) => (
                  <FloatingLabel
                    label="Número de Factura*"
                    type="number"
                    style={{ width: "100%" }}
                    initialValue={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.numeroFactura && <small className="text-danger form-error">{errors.numeroFactura.message}</small>}
            </div>
          </Col>

          <Col xs={12} sm={4} className="d-flex gap-2" style={{ gap: '10px' }}>
            <div style={{ flex: "0 0 100px" }} className="change-invoice-field">
              <Controller
                name="tipo"
                control={control}
                rules={{ required: "Seleccione tipo de identificación" }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    options={optionsIdentificacion}
                    onChange={(selected) => onChange(selected)}
                    value={value ? { value: value.value, label: value.label } : null}
                    isSearchable={false}
                    isClearable={false}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    classNamePrefix="change-invoice-select"
                  />
                )}
              />
              {errors.tipo && <small className="text-danger form-error">{errors.tipo.message}</small>}
            </div>

            <div style={{ flex: 1 }} className={`change-invoice-field ${filled?.numeroDocumento ? "is-filled" : ""}`}>
              <Controller
                name="numeroDocumento"
                control={control}
                rules={{
                  required: "Campo requerido",
                  pattern: {
                    value: /^\d{5,10}$/,
                    message: "Debe tener entre 5 y 10 dígitos"
                  }
                }}
                render={({ field }) => (
                  <FloatingLabel
                    label="Número de Documento*"
                    type="number"
                    style={{ width: "100%" }}
                    initialValue={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.numeroDocumento && <small className="text-danger form-error">{errors.numeroDocumento.message}</small>}
            </div>
          </Col>
        </Row>

        <Row className="gx-2">
          <Col xs={12} sm={4}>
            <div className={`change-invoice-field ${filled?.nombre ? "is-filled" : ""}`}>
              <Controller
                name="nombre"
                control={control}
                rules={{ required: "Campo requerido" }}
                render={({ field }) => (
                  <FloatingLabel
                    label="Nombre o Razón Social*"
                    type="text"
                    style={{ width: "100%" }}
                    initialValue={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.nombre && <small className="text-danger form-error">{errors.nombre.message}</small>}
            </div>
          </Col>

          <Col xs={12} sm={4}>
            <div className={`change-invoice-field ${filled?.correo ? "is-filled" : ""}`}>
              <Controller
                name="correo"
                control={control}
                rules={{
                  required: "Campo requerido",
                  pattern: {
                    value: /^\S+@\S+\.\S+$/,
                    message: "Correo inválido"
                  }
                }}
                render={({ field }) => (
                  <FloatingLabel
                    label="Correo electrónico*"
                    type="email"
                    style={{ width: "100%" }}
                    initialValue={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.correo && <small className="text-danger form-error">{errors.correo.message}</small>}
            </div>
          </Col>

          <Col xs={12} sm={4}>
            <div className={`change-invoice-field ${filled?.celular ? "is-filled" : ""}`}>
              <Controller
                name="celular"
                control={control}
                rules={{
                  maxLength: { value: 10, message: "No puede ser mayor a 10 dígitos" },
                  required: "Campo requerido",
                  validate: (value) => {
                    const v = String(value ?? "");
                    if (!v) return true;
                    if (v[0] !== "3") return "Debe iniciar con 3";
                    if (v.length > 10) return "No puede ser mayor a 10 dígitos";
                    if (v.length !== 10) return "Debe tener 10 dígitos";
                    return true;
                  }
                }}
                render={({ field }) => (
                  <FloatingLabel
                    label="Celular*"
                    type="number"
                    maxLength={10}
                    style={{ width: "100%" }}
                    initialValue={field.value ?? ""}
                    onChange={(e) => {
                      const raw = String(e.target.value ?? "");
                      const digits = raw.replace(/[^\d]/g, "");
                      const nextValue = digits.slice(0, 10);

                      if (nextValue.length > 0 && nextValue[0] !== "3") {
                        setError("celular", {
                          type: "manual",
                          message: "Debe iniciar con 3",
                        });
                      } else if (errors?.celular?.type === "manual") {
                        clearErrors("celular");
                      }

                      field.onChange(nextValue);
                    }}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.celular && <small className="text-danger form-error">{errors.celular.message}</small>}
            </div>
          </Col>
        </Row>

        <Row className="gx-2">
          <Col xs={12} sm={4}>
            <div className="change-invoice-field">
              <Controller
                name="ciudad"
                control={control}
                rules={{ required: "Seleccione una ciudad" }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    options={optionsCiudad}
                    onChange={onChange}
                    value={value}
                    placeholder="Ciudad*"
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    classNamePrefix="change-invoice-select"
                  />
                )}
              />
              {errors.ciudad && <small className="text-danger form-error">{errors.ciudad.message}</small>}
            </div>
          </Col>
        </Row>

        <Row className="gx-2">
          <Col xs={12} sm={4}>
            <Form.Group className="change-invoice-file-group">
              <Controller
                name="archivo"
                control={control}
                defaultValue={null}
                rules={{ required: "Adjunta la factura (PDF o imagen)" }}
                render={({ field }) => (
                  <UploadElectronicInvoiceFile
                    key={resetUpload}
                    value={field.value}
                    onChange={(files) => {
                      field.onChange(files);
                      setName(files);
                    }}
                    onMeta={(meta) => {
                      const prefix = meta?.invoiceId?.prefix ?? "";
                      const number = meta?.invoiceId?.number ?? "";
                      setValue("pdfPrefijo", prefix, { shouldValidate: true, shouldDirty: true });
                      setValue("pdfNumeroFactura", number, { shouldValidate: true, shouldDirty: true });
                    }}
                    isNameEmpty={name}
                  />
                )}
              />
              {errors.archivo && <small className="text-danger form-error">{errors.archivo.message}</small>}
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <div className="d-flex align-items-start gap-2">
            <Form.Check
              type="checkbox"
              id="change-invoice-acepta-politicas"
              {...register("acepta", { required: "Debes aceptar las políticas" })}
            />
            <label htmlFor="change-invoice-acepta-politicas" className="form-check-label mb-0" style={{ cursor: "pointer" }}>
              Acepto la{" "}
              <button
                type="button"
                className="btn btn-policy p-0 align-baseline text-decoration-underline"
                style={{ fontSize: "inherit", verticalAlign: "baseline", color: "#90D400 !important" }}
                onClick={(e) => openPoliciesModal("privacidad", e)}
              >
                Política de privacidad
              </button>{" "}
              y el{" "}
              <button
                type="button"
                className="btn btn-policy p-0 align-baseline text-decoration-underline"
                style={{ fontSize: "inherit", verticalAlign: "baseline", color: "#90D400 !important" }}
                onClick={(e) => openPoliciesModal("datos", e)}
              >
                Tratamiento de datos personales
              </button>
              .
            </label>
          </div>
          {errors.acepta && <small className="text-danger form-error">{errors.acepta.message}</small>}
        </Form.Group>
        <div className="d-flex gap-2 justify-content-end align-items-center mt-3 change-invoice-actions" style={{ gap: "10px" }}>
          {isDirty && (
            <div
              className="upload-btn btn-cancel change-invoice-btn-cancel"
              onClick={() => {
                setName(null);
                setResetUpload((prev) => prev + 1);
                reset();
              }}
            >
              Cancelar
            </div>
          )}
          <button
            type="submit"
            disabled={isLoading}
            aria-busy={isLoading}
            aria-label={isLoading ? "Enviando solicitud" : "Guardar"}
            className="upload-btn btn-send border-0 change-invoice-btn-submit"
          >
            {isLoading ? (
              <span className="change-invoice-submit-spinner-wrap" aria-hidden>
                <span className="container-sniper change-invoice-submit-spinner" />
              </span>
            ) : (
              "Guardar"
            )}
          </button>
        </div>
      </Form>
      <ModalCompanyPolicies
        showModal={showPoliciesModal}
        setShowModal={setShowPoliciesModal}
        activeTab={policiesActiveTab}
        setActiveTab={setPoliciesActiveTab}
        onAccept={() => {
          setValue("acepta", true);
          setShowPoliciesModal(false);
        }}
      />
    </>
  );
};

export default ChangeInvoiceComponent;